/**
 * Page preview protocol.
 *
 * Shared between the admin preview pane and the bridge that runs on the public
 * page, so both agree on the query parameter and message names. Mirrors the theme
 * preview protocol in lib/theme/tokens.js.
 */

import { findPageByPathWithId, findPageBySlug } from '../utils/pagePaths.js';
import { BLOCK_TYPES, GALLERY_LAYOUTS, slideshowContent, slideshowDraft } from './schema.js';
import { normalizeSlideshowSettings } from '../slideshow/settings.js';
import { isStackPage, patchBlockHtml, stackBodyFields, stackDraft } from './stack.js';
import { cleanViewerLook } from '../theme/viewer-look.js';

export const PAGE_PREVIEW_QUERY = 'pagePreview';

export const PAGE_READY_MESSAGE = 'portfolio:page-ready';
export const PAGE_DRAFT_MESSAGE = 'portfolio:page-draft';
export const PAGE_HTML_MESSAGE = 'portfolio:page-html';
export const IMAGE_TITLE_MESSAGE = 'portfolio:image-title';
/** Frame to editor: a block or tile was clicked. Editor to frame: mark this block selected. */
export const BLOCK_SELECT_MESSAGE = 'portfolio:block-select';
/** Frame to editor: insert a block of `type` after `afterId`. */
export const BLOCK_INSERT_MESSAGE = 'portfolio:block-insert';
/**
 * Frame to editor: the user asked to enter (`blockId`) or leave (empty) a block.
 * Editor to frame: the block that is open for editing now.
 */
export const BLOCK_EDIT_MESSAGE = 'portfolio:block-edit';
/** Frame to editor: reorder the tiles of a gallery block. */
export const BLOCK_GALLERY_MESSAGE = 'portfolio:block-gallery';
/** Frame to editor: drag-resized top/bottom spacing on a block. */
export const BLOCK_SPACE_MESSAGE = 'portfolio:block-space';
/** Frame to editor: editorial slideshow filmstrip actions (order / arrange / spreadGap). */
export const EDITORIAL_GALLERY_MESSAGE = 'portfolio:editorial-gallery';
/** Editor to frame: per-image analytics for tile overlays in the admin preview. */
export const PAGE_ANALYTICS_MESSAGE = 'portfolio:page-analytics';
/** Editor to frame: clear the tiles picked in masonry galleries (`ids` is []). */
export const GALLERY_SELECTION_MESSAGE = 'portfolio:gallery-selection';
/** Editor to frame: captions just saved for files (`captions` is { [fileId]: text }). */
export const FILE_CAPTIONS_MESSAGE = 'portfolio:file-captions';
/**
 * Editor to frame: how one file dropped on a masonry grid is getting on.
 * `key` names the frame's placeholder, `phase` is uploading, processing,
 * done or error, and `progress` is 0–100 while uploading.
 */
export const MASONRY_UPLOAD_STATUS = 'portfolio:masonry-upload-status';
/** Editor to frame: file records uploaded after the frame loaded (`files` is { [fileId]: record }). */
export const FILE_RECORDS_MESSAGE = 'portfolio:file-records';
/** Editor to frame: the open page can't be changed here (`readOnly`), so it shows as visitors see it. */
export const PAGE_READ_ONLY_MESSAGE = 'portfolio:page-read-only';
/** Frame to editor: which background set is currently looping in the preview. */
export const BACKGROUND_SET_MESSAGE = 'portfolio:background-set';
/** Editor to frame: emit held-back text edits now; the frame answers with PREVIEW_FLUSHED_MESSAGE. */
export const PREVIEW_FLUSH_MESSAGE = 'portfolio:preview-flush';
/** Frame to editor: every edit made before the flush request has been posted. */
export const PREVIEW_FLUSHED_MESSAGE = 'portfolio:preview-flushed';

let flushSeq = 0;

/**
 * Ask the preview frame to post any held-back edits, and resolve once it has.
 * Messages from one window arrive in order, so every edit is applied to the
 * draft before this resolves. Resolves anyway after `timeout` ms (no frame,
 * or a page without the bridge).
 * @param {Window|null|undefined} frameWindow
 * @param {{ timeout?: number }} [opts]
 * @returns {Promise<boolean>} true when the frame answered
 */
export function flushPreviewEdits(frameWindow, { timeout = 600 } = {}) {
    if (!frameWindow || typeof window === 'undefined') return Promise.resolve(false);
    flushSeq += 1;
    const id = `flush-${flushSeq}`;
    return new Promise((resolve) => {
        let timer = null;
        const finish = (answered) => {
            clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            resolve(answered);
        };
        function onMessage(event) {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === PREVIEW_FLUSHED_MESSAGE && event.data.id === id) finish(true);
        }
        window.addEventListener('message', onMessage);
        timer = setTimeout(() => finish(false), timeout);
        try {
            frameWindow.postMessage({ type: PREVIEW_FLUSH_MESSAGE, id }, window.location.origin);
        } catch {
            finish(false);
        }
    });
}

/** Fields the in-situ editor reads and writes for one page. Home has no slug or parent. */
export function pageDraftFrom(page, { home = false } = {}) {
    if (!page) return null;
    const draft = {
        name: page.name || '',
        content: page.content || '',
        contentMaxWidth: page.contentMaxWidth ?? 65,
        isPublic: page.isPublic !== false,
        // Drawer listing. Absent means shown — same for home and other pages.
        showInNav: page.showInNav !== false,
        sitemap: page.sitemap !== false,
        gallery: page.gallery ? JSON.parse(JSON.stringify(page.gallery)) : { type: 'none', images: [] },
        themeBackground: page.themeBackground || null,
        themeText: page.themeText || null,
        themeSideMenuBackground: page.themeSideMenuBackground || null,
        themeSideMenuText: page.themeSideMenuText || null,
        themeHeaderBackground: page.themeHeaderBackground || null,
        themeHeaderText: page.themeHeaderText || null,
        themeCaption: page.themeCaption || null,
        viewerLook: cleanViewerLook(page.viewerLook),
        backgroundImage: page.backgroundImage ? JSON.parse(JSON.stringify(page.backgroundImage)) : null,
        advanceUrl: page.advanceUrl || null,
        og: page.og ? { ...page.og } : {},
        ...(Array.isArray(page.blocks) ? { blocks: JSON.parse(JSON.stringify(page.blocks)) } : {}),
    };
    if (!home) {
        draft.slug = page.slug || page.url || '';
        draft.parentId = page.parentId || page.parentUrl || null;
    }
    return draft.gallery?.type === GALLERY_LAYOUTS.SLIDESHOW
        ? slideshowDraft(draft, page.slideshow)
        : draft;
}

/**
 * The home page with nothing on it, for a version started from scratch. It
 * keeps the site's name and share card, which are about the site, not the page.
 */
export function emptyHomeDraft(home) {
    const draft = pageDraftFrom(home || {}, { home: true });
    return {
        ...draft,
        content: '',
        gallery: { type: 'none', images: [] },
        backgroundImage: null,
        advanceUrl: null,
        blocks: [],
    };
}

/**
 * Body for PUT /api/admin/pages or /api/admin/home.
 * The page route reads `newSlug`, and only when the slug actually changed.
 * Gallery is the full object. Blocks are included only when this draft has them.
 */
export function pageBodyForSave(draft, { home = false, savedSlug = '' } = {}) {
    if (!draft) return null;
    const body = {
        name: draft.name || '',
        content: draft.content ?? '',
        contentMaxWidth: draft.contentMaxWidth,
        isPublic: draft.isPublic !== false,
        showInNav: draft.showInNav !== false,
        sitemap: draft.sitemap !== false,
        gallery: draft.gallery ? JSON.parse(JSON.stringify(draft.gallery)) : { type: 'none', images: [] },
        themeBackground: draft.themeBackground || null,
        themeText: draft.themeText || null,
        themeSideMenuBackground: draft.themeSideMenuBackground || null,
        themeSideMenuText: draft.themeSideMenuText || null,
        themeHeaderBackground: draft.themeHeaderBackground || null,
        themeHeaderText: draft.themeHeaderText || null,
        themeCaption: draft.themeCaption || null,
        viewerLook: cleanViewerLook(draft.viewerLook),
        backgroundImage: draft.backgroundImage || null,
        advanceUrl: draft.advanceUrl || null,
        // The routes merge og field-by-field and skip absent keys, so a cleared
        // field must be sent as null or the saved value survives.
        og: {
            ...(draft.og || {}),
            image: draft.og?.image || null,
            description: draft.og?.description || null,
            title: draft.og?.title || null,
        },
    };
    if (Array.isArray(draft.blocks) && isStackPage(draft)) {
        Object.assign(body, stackBodyFields(draft));
    } else if (Array.isArray(draft.blocks)) {
        const overlay = draft.gallery?.type === GALLERY_LAYOUTS.BACKGROUND
            || draft.gallery?.type === GALLERY_LAYOUTS.SLIDESHOW;
        if (overlay) {
            // Vectors (and extra text rows) live on page.blocks for visitor render.
            // Always send the overlay list — including [] so a cleared overlay sticks.
            body.blocks = draft.blocks.filter(
                (block) => block?.type === BLOCK_TYPES.RICHTEXT || block?.type === BLOCK_TYPES.VECTOR,
            );
            // page.content mirrors the first Text row, and a removed Text row
            // must clear it or the overlay synthesizes the old copy back.
            body.content = slideshowContent(body.blocks);
        } else {
            body.blocks = draft.blocks;
        }
    }
    if (draft.gallery?.type === GALLERY_LAYOUTS.SLIDESHOW) {
        body.slideshow = normalizeSlideshowSettings(draft.slideshow);
    }
    if (!home) {
        body.parentId = draft.parentId || null;
        if (draft.slug && draft.slug !== savedSlug) body.newSlug = draft.slug;
    }
    return body;
}

/**
 * The page shown at a public path. Home is `/`.
 * With `stack`, text, masonry and directory pages come back compiled into blocks.
 */
export function pageTargetForPath(content, path, { stack = false } = {}) {
    const clean = (path || '/').split('?')[0];
    const shape = (page) => (stack ? stackDraft(page) : page);
    if (clean === '/' || clean === '') {
        return { id: 'home', page: shape(pageDraftFrom(content?.home || {}, { home: true })) };
    }
    const urlPath = clean.replace(/^\//, '');
    const pages = content?.pages || {};
    let found = findPageByPathWithId(urlPath, pages);
    if (!found && !urlPath.includes('/')) found = findPageBySlug(urlPath, pages);
    if (!found?.id) return null;
    return { id: found.id, page: shape(pageDraftFrom({ ...found.page, id: found.id })) };
}

function contentSourceBlock(blocks) {
    if (!Array.isArray(blocks)) return null;
    return blocks.find((block) => block.type === 'richtext')
        || blocks.find((block) => block.type === 'gallery')
        || null;
}

/** Write HTML into page.content, or into one rich-text block. */
export function patchPageHtml(page, path, html) {
    if (!page || typeof html !== 'string') return page;
    if (!path || path === 'content') {
        const source = contentSourceBlock(page.blocks);
        if ((page.content || '') === html && (!source || (source.html || '') === html)) return page;
        const next = { ...page, content: html };
        // Save rebuilds content from this block, so it must carry the edit even
        // when the two had drifted apart; otherwise the old text comes back.
        if (source) {
            next.blocks = page.blocks.map((block) => (
                block.id === source.id ? { ...block, html } : block
            ));
        }
        return next;
    }
    const match = /^blocks\.(.+)\.html$/.exec(path);
    if (!match || !Array.isArray(page.blocks)) return page;
    const id = match[1];
    const blocks = patchBlockHtml(page.blocks, id, html);
    if (blocks === page.blocks) return page;
    const next = { ...page, blocks };
    const source = contentSourceBlock(blocks);
    if (source && source.id === id) next.content = html;
    return next;
}

export default {
    PAGE_PREVIEW_QUERY,
    PAGE_READY_MESSAGE,
    PAGE_DRAFT_MESSAGE,
    PAGE_HTML_MESSAGE,
    IMAGE_TITLE_MESSAGE,
    BLOCK_SELECT_MESSAGE,
    BLOCK_INSERT_MESSAGE,
    BLOCK_EDIT_MESSAGE,
    BLOCK_SPACE_MESSAGE,
    EDITORIAL_GALLERY_MESSAGE,
    BACKGROUND_SET_MESSAGE,
    pageDraftFrom,
    pageBodyForSave,
    pageTargetForPath,
    patchPageHtml,
};
